import * as React from "react"

/**
 * Story 24.2b — the one internal live-region primitive, shared by the
 * announcer (`./announcer.tsx`) and the toast region (`./sonner.tsx`).
 *
 * - Always mounted and empty from start-up: a live region that appears
 *   together with its text is often not read at all.
 * - Each new `nonce` clears the text, then writes `message` on a 50ms tick,
 *   so the same message sent twice is read twice.
 * - Polite is `role="status"`; assertive is `aria-live="assertive"`. Both are
 *   atomic and screen-reader only.
 */

const LIVE_REGION_TICK_MS = 50

type LiveRegionProps = {
  politeness: "polite" | "assertive"
  message: string
  /** Bump to (re-)announce `message`. 0 means nothing has been sent yet. */
  nonce: number
} & Omit<React.ComponentProps<"div">, "children" | "role" | "nonce">

function LiveRegion({ politeness, message, nonce, className, ...props }: LiveRegionProps) {
  const [text, setText] = React.useState("")
  const messageRef = React.useRef(message)
  messageRef.current = message

  React.useEffect(() => {
    if (!nonce) return
    setText("")
    const timer = window.setTimeout(() => setText(messageRef.current), LIVE_REGION_TICK_MS)
    return () => window.clearTimeout(timer)
  }, [nonce])

  const roleProps =
    politeness === "polite"
      ? ({ role: "status", "aria-live": "polite" } as const)
      : ({ "aria-live": "assertive" } as const)

  return (
    <div {...props} {...roleProps} aria-atomic="true" className={className ? `sr-only ${className}` : "sr-only"}>
      {text}
    </div>
  )
}

export { LiveRegion }
export type { LiveRegionProps }
